import type { PropertyCalendarOrphanGap } from '@/api/client'
import { STAY_FOCUS_RING } from '@/components/calendar/stay-bars'
import {
  STAY_END_FRACTION,
  STAY_START_FRACTION,
  parseStay,
  toCalendarDate,
  type CalendarReservation,
  type EdgeInset,
  type MonthBar,
} from '@/components/calendar/month-grid'
import { describeGapSpan, nightsLabel } from '@/lib/gap-events'
import { addDays, format } from 'date-fns'
import { isReservationCancelled } from '@/lib/reservation-utils'
import { cn } from '@/lib/utils'

/**
 * Empty nights between two bookings, drawn as a hatched amber bar.
 *
 * A pattern, not just a colour, so it cannot be mistaken for a stay (solid
 * channel colour, a guest's name) or for a block (grey, dashed): the stripes
 * say "nothing is here" and the amber says "worth a look".
 */
export const GAP_BAR_CLASSES = cn(
  'border-amber/70 font-medium text-text',
  'bg-[repeating-linear-gradient(135deg,color-mix(in_srgb,var(--color-amber)_34%,transparent)_0_4px,color-mix(in_srgb,var(--color-amber)_8%,transparent)_4px_8px)]'
)

/** The days stays the grid draws leave and arrive on, as `YYYY-MM-DD`. */
interface StayEdgeDays {
  checkOuts: Set<string>
  checkIns: Set<string>
}

function stayEdgeDays(stays: CalendarReservation[]): StayEdgeDays {
  const checkOuts = new Set<string>()
  const checkIns = new Set<string>()
  for (const stay of stays) {
    // The same stays the grid draws: a cancelled one has no bar to sit against.
    if (isReservationCancelled(stay)) continue
    const parsed = parseStay(stay)
    if (!parsed) continue
    checkIns.add(format(parsed.checkInDate, 'yyyy-MM-dd'))
    checkOuts.add(format(parsed.checkOutDate, 'yyyy-MM-dd'))
  }
  return { checkOuts, checkIns }
}

/**
 * A gap fits exactly between the two stays around it: it begins where the
 * guest before checks out (25% across that day) and ends where the next guest
 * arrives (75% across their check-in day), the mirror of a stay's own inset.
 *
 * Which sides have a stay is read from the stays on the grid, not from the
 * gap's optional `stages`: a gap with no assessed stage is still next to the
 * same bars, and the bar must sit against them either way. Against a block or
 * a held night (or a stay that is not drawn) that side runs flush to the day
 * boundary.
 */
function gapEdgeInset(gap: PropertyCalendarOrphanGap, edges: StayEdgeDays): EdgeInset {
  const nextDay = format(addDays(toCalendarDate(gap.last_night), 1), 'yyyy-MM-dd')
  return {
    start: edges.checkOuts.has(gap.first_night) ? STAY_END_FRACTION : 0,
    end: edges.checkIns.has(nextDay) ? STAY_START_FRACTION : 0,
  }
}

interface GapBarOptions {
  /** The gap to ring (its first night), if any. */
  focusedFirstNight?: string | null
  onOpen: (gap: PropertyCalendarOrphanGap) => void
  /** The stays drawn on the same grid: the gap's edges are fitted against them. */
  stays: CalendarReservation[]
}

export function buildGapBars(
  gaps: PropertyCalendarOrphanGap[],
  { focusedFirstNight = null, onOpen, stays }: GapBarOptions
): MonthBar[] {
  const edges = stayEdgeDays(stays)
  return gaps.flatMap(gap => {
    const start = toCalendarDate(gap.first_night)
    // `last_night` is inclusive; the grid wants the first day NOT covered.
    const endExclusive = addDays(toCalendarDate(gap.last_night), 1)
    const span = describeGapSpan(gap)
    const continues = [
      gap.starts_before_range ? 'continues from the previous month' : null,
      gap.ends_after_range ? 'continues into the next month' : null,
    ].filter(Boolean)
    const detail = [gap.actionable ? 'An offer is available' : null, ...continues]
      .filter(Boolean)
      .join('. ')
    return [
      {
        key: `gap-${gap.first_night}`,
        start,
        endExclusive,
        edgeInset: gapEdgeInset(gap, edges),
        label: nightsLabel(gap.nights),
        title: `Orphan gap: ${span}${detail ? `. ${detail}` : ''}`,
        ariaLabel: `Open orphan gap: ${span}${detail ? `. ${detail}` : ''}`,
        testId: 'calendar-gap-bar',
        data: { 'data-gap-first-night': gap.first_night },
        className: cn(GAP_BAR_CLASSES, focusedFirstNight === gap.first_night && STAY_FOCUS_RING),
        onClick: () => onOpen(gap),
      },
    ]
  })
}
